import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import usePortal from 'react-cool-portal';
import { BlockSpinner } from '../helpers/Spiners';
import { ReactComponent as CrossIcon } from '../assets/images/cross.svg';
import { ReactComponent as CrossOutlineIcon } from '../assets/images/cross-outline.svg';
import { ReactComponent as ArrowDown } from '../assets/images/arrow-down.svg';

const SiteItem = ({ site, remove }) => (
  <div className="site-item">
    {site.name}
    <CrossOutlineIcon className="" onClick={() => remove(site)} />
  </div>
);

SiteItem.propTypes = {
  site: PropTypes.instanceOf(Object).isRequired,
  remove: PropTypes.func.isRequired
};

const checkIsFull = () => {
  // flex wrap items check
  // if items are wrapped or not
  const parent = document.getElementsByClassName('value-block true')[0];
  if (!parent?.children) return false;
  const { children } = parent;
  if (!children?.length) return false;
  const chArray = [...children];
  return !!chArray?.filter(x => {
    return x.offsetTop > 0;
  }).length;
};

const Value = ({ value, active, remove, hide, isCOT }) => {
  const [placeholder, setPlaceholder] = useState({
    text: isCOT ? 'Choose Class of Trade' : 'Search Facilities',
    force: false
  });

  const placeholderCalc = async () => {
    const isFull = checkIsFull();
    if (!value?.length) {
      setPlaceholder({ text: isCOT ? 'Choose Class of Trade' : 'Search Facilities', force: false });
      return;
    }
    if (isFull) {
      setPlaceholder({
        text: `${value?.length} ${isCOT ? 'classes of trade' : 'facilities'} selected`,
        force: true
      });
      return;
    }
    setPlaceholder({ text: isCOT ? 'Choose Class of Trade' : 'Search Facilities', force: false });
  };

  useEffect(() => {
    placeholderCalc();
  }, [value]);

  return (
    <>
      <div className={`value-block ${active}`}>
        {(!value?.length || placeholder.force) && (
          <span className="placeholder">{placeholder.text}</span>
        )}
        {!!value?.length && value.map(v => <SiteItem key={v.id} site={v} remove={remove} />)}
      </div>
      <div className="block-icons">
        {!!value?.length && (
          <button className="crossIcon reset-btn" onClick={hide} type="button">
            <CrossIcon />
          </button>
        )}
        <ArrowDown className={`arrowDown ${active && 'rotate-180'}`} />
      </div>
    </>
  );
};

Value.propTypes = {
  value: PropTypes.instanceOf(Array).isRequired,
  active: PropTypes.bool.isRequired,
  remove: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  isCOT: PropTypes.bool.isRequired
};

const resolveElement = el => {
  const clName = 'placeholder-block';
  const closeClName = 'crossIcon';
  const isClose = false;
  if (el.target.classList.contains(clName)) return el.target;
  let currentEl = el.target.parentElement;
  while (currentEl) {
    if (currentEl.classList.contains(clName)) {
      return { el: currentEl.parentElement, isClose };
    }
    if (currentEl.classList.contains(closeClName)) {
      return { el: currentEl, isClose: true };
    }
    currentEl = currentEl.parentElement;
  }
  return { el, isClose };
};

const SearchSelectFacilityDrop = ({ disabled, items, value, onChange, loading, isCOT }) => {
  const [style, setStyle] = useState({});
  const [filterVal, setFilter] = useState('');

  const onFilterChange = filter => {
    if (filter !== filterVal) {
      setFilter(filter);
    }
  };

  const filteredItems = useMemo(() => {
    const preparedFilter = filterVal.toLowerCase();
    const unsorted = filterVal
      ? items.filter(s => s.name.toLowerCase().includes(preparedFilter))
      : items;
    return unsorted.sort((a, b) => {
      const aPresent = value.find(x => x.id === a.id);
      const bPresent = value.find(x => x.id === b.id);
      if (aPresent && !bPresent) {
        return -1;
      }
      if (!aPresent && bPresent) {
        return 1;
      }
      return 0;
    });
  }, [items, filterVal, value]);

  const { Portal, isShow, show, hide } = usePortal({
    defaultShow: false,
    onShow: e => {
      e.persist();
      const bodyRect = document.body.getBoundingClientRect();
      const { el, isClose } = resolveElement(e);
      if (isClose) {
        onChange([]);
        hide();
        return;
      }
      const elemRect = el.getBoundingClientRect();
      const offset = elemRect.top - bodyRect.top;
      const { left } = elemRect;
      setStyle({ width: `${elemRect.width}px`, top: `${offset + 42}px`, left: `${left}px` });
    }
  });

  const toggleMenu = e => {
    if (!disabled) show(e);
  };

  const itemClick = item => {
    const isPrecent = value.find(s => s.id === item.id);
    if (isPrecent) {
      onChange(value.filter(s => s.id !== item.id));
      return;
    }
    onChange([...value, item]);
  };

  return (
    <div className={`search-select-facility-input ${disabled && 'disabled'}`}>
      {/* eslint-disable-next-line */}
      <div className="placeholder-block" onClick={toggleMenu}>
        <Value
          value={value}
          disabled={disabled}
          remove={itemClick}
          hide={hide}
          active={isShow}
          isCOT={isCOT}
        />
      </div>
      <Portal>
        <div className={`facility-menu${isShow ? ' show ' : ' '}`} style={style}>
          <div className="filter">
            <input
              type="text"
              value={filterVal}
              onChange={event => onFilterChange(event.target.value)}
              className="search-input"
              placeholder="Search for"
            />
          </div>

          {loading && <BlockSpinner />}
          <div className="facilities-block protean-scroll">
            {!loading &&
              filteredItems?.map(item => {
                return item.name ? (
                  <button
                    type="button"
                    key={item.id}
                    className="facility-block"
                    onClick={() => itemClick(item)}
                  >
                    <input
                      key={item.id}
                      type="checkbox"
                      onClick={() => {}}
                      onChange={() => {}}
                      checked={!!value?.find(s => s.id === item.id)}
                      className="facility-checkbox"
                    />
                    <span className="facility-name">{item.name}</span>
                  </button>
                ) : (
                  ''
                );
              })}
          </div>
          {!loading && items?.length === 0 && (
            <span className="no-records">There are no available items</span>
          )}
        </div>
      </Portal>
    </div>
  );
};

SearchSelectFacilityDrop.propTypes = {
  disabled: PropTypes.bool.isRequired,
  items: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Array).isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isCOT: PropTypes.bool
};

SearchSelectFacilityDrop.defaultProps = {
  loading: false,
  isCOT: false
};

export default SearchSelectFacilityDrop;
